import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IInboxMessagesResponse } from 'Modules/Core/Applicants/ApplicantInbox/ApplicantInboxModel';
import { IHtmlInterviewKit, IResumeFocusedAIQuestions } from 'Modules/Core/Applicants/ApplicantProfile/ApplicantProfileModel';
import {
    IApplicant,
    IApplicantProfileState,
    IApplicantRatingUpdate,
    IApplicantStageParams,
    IGetApplicantDetailsParams,
    ISuggestedQuestions,
    IUpdateApplicantPassword,
    IUpdateApplicantProfile,
    IActivityLogParams,
} from 'Modules/Core/Applicants/ApplicantsModel';
import { AxiosResponse } from 'axios';
import httpAdapterInstance from 'configs/HttpAdapterConfig';
import { PURGE } from 'redux-persist';
import { EmployerApiEndpoints } from 'shared/ApiEndpoints';
import { IBaseResponse } from 'shared/SharedModels';
import { DefaultAPIErrorMsg } from 'shared/constants';

const initialApplicantProfileState: IApplicantProfileState = {};

export const getApplicantDetails = createAsyncThunk<IBaseResponse<IApplicant>, IGetApplicantDetailsParams, { rejectValue: IBaseResponse }>(
    'getApplicantDetails',
    async ({ jobId, applicationId }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .get(`${EmployerApiEndpoints.EMPLOYER_JOBS}/${jobId}/applicants/${applicationId}`)
            .then((response: AxiosResponse<IBaseResponse<IApplicant>>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

export const getFullFingerPrintImage = createAsyncThunk<string, { applicantId: number }, { rejectValue: IBaseResponse }>(
    'getFullFingerPrintImage',
    async ({ applicantId }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .get(`${EmployerApiEndpoints.APPLICANTS}/${applicantId}/fingerprints/full`)
            .then((response: AxiosResponse<string>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

export const getSuggestedQuestions = createAsyncThunk<IBaseResponse<ISuggestedQuestions>, { applicantId: number }, { rejectValue: IBaseResponse }>(
    'getSuggestedQuestions',
    async ({ applicantId }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .get(`${EmployerApiEndpoints.APPLICANTS}/${applicantId}/questions`)
            .then((response: AxiosResponse<IBaseResponse<ISuggestedQuestions>>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

export const getApplicantResume = createAsyncThunk<{ url: string }, { applicantId: number }, { rejectValue: IBaseResponse }>(
    'getApplicantResume',
    async ({ applicantId }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .get(`${EmployerApiEndpoints.APPLICANTS}/${applicantId}/resume`)
            .then((response: AxiosResponse<{ url: string }>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

export const updateApplicantProfile = createAsyncThunk<IBaseResponse<IUpdateApplicantProfile>, IUpdateApplicantProfile, {
    rejectValue: IBaseResponse
}>(
    "updateApplicantProfile",
    async ({ applicantId, first_name, last_name, middle_name }: IUpdateApplicantProfile, { rejectWithValue }) => {
        return await httpAdapterInstance
            .put(`${EmployerApiEndpoints.UPDATE_APPLICANT_PROFILE}/${applicantId}`, { first_name, last_name, middle_name })
            .then((response) => response.data)
            .catch((error) => {
                throw rejectWithValue(error);
            });
    }
);

export const updateApplicantEmail = createAsyncThunk<IBaseResponse, {
    newEmail: string,
    otp: number
}, {
    rejectValue: IBaseResponse
}>(
    "updateApplicantProfile",
    async ({ newEmail, otp }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .put(`${EmployerApiEndpoints.UPDATE_APPLICANT_PROFILE}`, { newEmail, otp })
            .then((response) => response.data)
            .catch((error) => {
                throw rejectWithValue(error);
            });
    }
);

export const updateApplicantPassword = createAsyncThunk<IBaseResponse, IUpdateApplicantPassword, {
    rejectValue: IBaseResponse
}>(
    "updateApplicantPassword",
    async ({ confirmPassword, newPassword, oldPassword }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .put(`${EmployerApiEndpoints.UPDATE_APPLICANT_PASSWORD}`, { confirmPassword, newPassword, oldPassword })
            .then((response) => response.data)
            .catch((error) => {
                throw rejectWithValue(error);
            });
    }
);

export const changeApplicantStageInProfile = createAsyncThunk<IBaseResponse, IApplicantStageParams, { rejectValue: IBaseResponse }>(
    'changeApplicantStageInProfile',
    async ({ applicantId, payload }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .put(`${EmployerApiEndpoints.APPLICANTS}/${applicantId}/stage`, payload)
            .then((response: AxiosResponse<IBaseResponse>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

export const fetchActivityLogs = createAsyncThunk<IBaseResponse, IActivityLogParams, { rejectValue: IBaseResponse }>(
    'fetchActivityLogs',
    async (params, { rejectWithValue }) => {
        return await httpAdapterInstance
            .get(`${EmployerApiEndpoints.ACTIVITY}/logs`, {
                params: Object.fromEntries(
                    Object.entries(params).filter(([_, value]) => value !== null)
                )
            })
            .then((response: AxiosResponse<IBaseResponse>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

export const updateApplicantRatingInProfile = createAsyncThunk<IBaseResponse, IApplicantRatingUpdate, { rejectValue: IBaseResponse }>(
    'updateApplicantRatingInProfile',
    async ({ applicantId, payload }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .put(`${EmployerApiEndpoints.APPLICANTS}/${applicantId}/rating`, payload)
            .then((response: AxiosResponse<IBaseResponse>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

export const fetchCandidateProfileEEOQuestionsAnswers = createAsyncThunk<
  IBaseResponse<{ [idx: number]: string }>, 
  { candidateId: number },
  { rejectValue: IBaseResponse }
>(
  "fetchCandidateProfileEEOQuestionsAnswers",
  async ({ candidateId }, { rejectWithValue }) => {
    return httpAdapterInstance
      .get(`${EmployerApiEndpoints.JOB_REQUIREMENTS}/${candidateId}/eeoProfileQuestionAnswers`)
      .then((response: AxiosResponse<IBaseResponse<{ [idx: number]: string }>>) => {
        return response.data;
      })
      .catch((error) => {
        return rejectWithValue(error.response?.data || error.message);
      });
  }
);


export const fetchCandidateProfileAdditionalQuestionAnswers = createAsyncThunk<
  IBaseResponse<{ [questionId: number]: { answer: string; autoReject: boolean } }>, 
  { candidateId: number },
  { rejectValue: IBaseResponse }
>(
  "fetchCandidateProfileAdditionalQuestionAnswers",
  async ({ candidateId }, { rejectWithValue }) => {
    return httpAdapterInstance
      .get(`${EmployerApiEndpoints.JOB_REQUIREMENTS}/${candidateId}/additionalQuestionsApplicationQuestionAnswers`)
      .then((response: AxiosResponse<IBaseResponse<{ [questionId: number]: { answer: string; autoReject: boolean } }>>) => {
        return response.data;
      })
      .catch((error) => {
        return rejectWithValue(error.response?.data || error.message);
      });
  }
);

export const fetchCandidateExtraDocument = createAsyncThunk<string, { fileKey: string },{ rejectValue: IBaseResponse }>(
  "fetchCandidateExtraDocument",
  async ({ fileKey }, { rejectWithValue }) => {
    return httpAdapterInstance
      .get(`${EmployerApiEndpoints.CANDIDATES}/candidate-document-url?fileKey=${encodeURIComponent(fileKey)}`)
      .then((response: AxiosResponse<{ candidateDocumentURL: string }>) => {
        if (response.data?.candidateDocumentURL) {
          return response.data.candidateDocumentURL; // Return only the URL
        } else {
          return Promise.reject({ message: "Invalid response format", statusCode: 500 });
        }
      })
      .catch((error) => {
        return rejectWithValue(error.response?.data || { message: "Network error", statusCode: 500 });
      });
  }
);

export const fetchAndGenerateAiResumeFocusedQuestions = createAsyncThunk<IBaseResponse<IResumeFocusedAIQuestions>, { resume_id: number,  candidate_employerjobs_id: number}, 
    { rejectValue: IBaseResponse }>(
    'fetchAndGenerateAiResumeFocusedQuestions',
    async ({ resume_id, candidate_employerjobs_id }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .post(`${EmployerApiEndpoints.AI_CANDIDATE_QUESTIONS}`, {resume_id, candidate_employerjobs_id})
            .then((response: AxiosResponse<IBaseResponse<IResumeFocusedAIQuestions>>) => response?.data)
            .catch((error) => {
                return rejectWithValue(error.response.data);
            });
    }
);

export const saveInterviewQuestionResponses = createAsyncThunk<
  IBaseResponse, 
  { candidate_employerjobs_id: number; responses: Record<string, any>; questions: string[] }, 
  { rejectValue: IBaseResponse }
>(
  'saveInterviewQuestionResponses',
  async ({ candidate_employerjobs_id, responses, questions }, { rejectWithValue }) => {
    return httpAdapterInstance
      .post(`${EmployerApiEndpoints.AI_CANDIDATE_QUESTIONS}save-responses`, { 
        candidate_employerjobs_id, 
        responses,
        questions 
      })
      .then((response: AxiosResponse<IBaseResponse>) => response.data)
      .catch((error) => {
        return rejectWithValue(error.response?.data || { message: 'Failed to save responses' });
      });
  }
);

export const fetchAndGenerateAiInterivewKit= createAsyncThunk<IBaseResponse<IHtmlInterviewKit>, { resume_id: number,  candidate_employerjobs_id: number}, 
    { rejectValue: IBaseResponse }>(
    'fetchAndGenerateAiInterivewKit',
    async ({ resume_id, candidate_employerjobs_id }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .post(`${EmployerApiEndpoints.AI_INTERVIEW_KIT}`, {resume_id, candidate_employerjobs_id})
            .then((response: AxiosResponse<IBaseResponse<IHtmlInterviewKit>>) => response?.data)
            .catch((error) => {
                return rejectWithValue(error.response.data);
            });
    }
);

export const getApplicantInbox = createAsyncThunk<IInboxMessagesResponse, { candidateEmployerJobId: number },{ rejectValue: IBaseResponse } >(
    'getApplicantInbox',({ candidateEmployerJobId }, { rejectWithValue }) => {
        return httpAdapterInstance
        .get<IInboxMessagesResponse>(`${EmployerApiEndpoints.APPLICANTS}/get-applicant-inbox/${candidateEmployerJobId}`)
        .then((response: AxiosResponse<IInboxMessagesResponse>) => response.data)
        .catch((error) => rejectWithValue(error.response?.data));
    }
);
  

const applicantProfileSlice = createSlice({
    name: 'applicantProfile',
    initialState: initialApplicantProfileState,
    reducers: {
        resetApplicantProfile: (state) => {
            state = { ...initialApplicantProfileState }
        },
        resetGetApplicantResume: (state) => {
            state.getApplicantResumeStatus = 'idle';
            state.getApplicantResumeResponse = '';
            state.resumeS3Url = undefined;
        },
        resetUpdateApplicantProfile: (state) => {
            state.updateApplicantProfileApiStatus = 'idle';
            state.updateApplicantProfileApiResponse = '';
        },
        resetGetApplicantDetails: (state) => {
            state.getApplicantDetailsStatus = 'idle';
            state.getApplicantDetailsResponse = '';
        },
        resetChangeApplicantStageInProfile: (state) => {
            if (state.applicantDetails) {
                state.applicantDetails.changeStateStatus = 'idle';
                state.applicantDetails.changeStateResponse = '';
            }
        },
        resetUpdateApplicantRatingInProfile: (state) => {
            if (state.applicantDetails) {
                state.applicantDetails.updateRatingStatus = 'idle';
                state.applicantDetails.updateRatingResponse = '';
            }
        },
        resetAiCandidateInterviewQuestions: (state) => {
            state.aiInterviewQuestions = undefined;
            state.fetchAndGenerateAiResumeFocusedQuestionsStatus = 'idle'; 
            state.fetchAndGenerateAiResumeFocusedQuestionsResponse = ''; 
        },
        resetAiCandidateInterviewKit: (state) => {
            state.aiInterviewHTMLKit = undefined;
            state.fetchAndGenerateAiInterivewKitStatus = 'idle'; 
            state.fetchAndGenerateAiInterivewKitResponse = ''; 
        },
        resetApplicantInbox: (state) => {
            state.applicantInboxMessages = undefined;
            state.getApplicantInboxStatus = 'idle'; 
            state.getApplicantInboxResponse = ''; 
        },
    },
    extraReducers: (builder) => {
        // On Store PURGE reset the state
        builder.addCase(PURGE, () => {
            return initialApplicantProfileState;
        });
        // get applicant details
        builder.addCase(getApplicantDetails.pending, (state) => {
            state.getApplicantDetailsStatus = 'pending';
        });
        builder.addCase(getApplicantDetails.fulfilled, (state, action) => {
            state.getApplicantDetailsStatus = 'success';
            state.applicantDetails = action.payload.data;
            if (state.applicantDetails) {
                state.applicantDetails.fullName = `${state.applicantDetails?.firstName ?? ''} ${state.applicantDetails?.lastName ?? ''}`;
            }
        });
        builder.addCase(getApplicantDetails.rejected, (state, action) => {
            state.getApplicantDetailsStatus = 'failed';
            state.getApplicantDetailsResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });
        // get fingerprint image
        builder.addCase(getFullFingerPrintImage.pending, (state) => {
            state.getFullFingerPrintImageStatus = 'pending';
        });
        builder.addCase(getFullFingerPrintImage.fulfilled, (state, action) => {
            state.getFullFingerPrintImageStatus = 'success';
            state.fullFingerPrintImage = action.payload;
        });
        builder.addCase(getFullFingerPrintImage.rejected, (state, action) => {
            state.getFullFingerPrintImageStatus = 'failed';
            state.getFullFingerPrintImageResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });
        // get suggested questions
        builder.addCase(getSuggestedQuestions.pending, (state) => {
            state.getSuggestedQuestionsStatus = 'pending';
        });
        builder.addCase(getSuggestedQuestions.fulfilled, (state, action) => {
            state.getSuggestedQuestionsStatus = 'success';
            state.suggestedQuestions = action.payload?.data;
        });
        builder.addCase(getSuggestedQuestions.rejected, (state, action) => {
            state.getSuggestedQuestionsStatus = 'failed';
            state.getSuggestedQuestionsResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });

        // get applicant resume
        builder.addCase(getApplicantResume.pending, (state) => {
            state.getApplicantResumeStatus = 'pending';
        });
        builder.addCase(getApplicantResume.fulfilled, (state, action) => {
            state.getApplicantResumeStatus = 'success';
            state.resumeS3Url = action.payload?.url;
        });
        builder.addCase(getApplicantResume.rejected, (state, action) => {
            state.getApplicantResumeStatus = 'failed';
            state.getApplicantResumeResponse = action.payload?.message;
        });

        // update applicant profile
        builder.addCase(updateApplicantProfile.pending, (state) => {
            state.updateApplicantProfileApiStatus = "pending";
        });
        builder.addCase(updateApplicantProfile.fulfilled, (state, action) => {
            state.updateApplicantProfileApiStatus = "success";
            state.updateApplicantProfileApiResponse = action.payload.message ?? DefaultAPIErrorMsg;
        });
        builder.addCase(updateApplicantProfile.rejected, (state) => {
            state.updateApplicantProfileApiStatus = "failed";
        });

        // update applicant password
        builder.addCase(updateApplicantPassword.pending, (state) => {
            state.updatePasswordApiStatus = "pending";
        });
        builder.addCase(updateApplicantPassword.fulfilled, (state, action) => {
            state.updatePasswordApiStatus = "success";
            state.updatePasswordApiResponse = action.payload.message ?? DefaultAPIErrorMsg;
        });
        builder.addCase(updateApplicantPassword.rejected, (state) => {
            state.updatePasswordApiStatus = "failed";
        });
        // change applicant stage
        builder.addCase(changeApplicantStageInProfile.pending, (state, action) => {
            if (state.applicantDetails) {
                state.applicantDetails.changeStateStatus = 'pending';
            }
        });
        builder.addCase(changeApplicantStageInProfile.fulfilled, (state, action) => {
            if (state.applicantDetails) {
                state.applicantDetails.changeStateStatus = 'success';
                state.applicantDetails.changeStateResponse = action?.payload?.message;
                state.applicantDetails.jobStage = action.meta.arg.payload.stage;
            }
        });
        builder.addCase(changeApplicantStageInProfile.rejected, (state, action) => {
            if (state.applicantDetails) {
                state.applicantDetails.changeStateStatus = 'failed';
                state.applicantDetails.changeStateResponse = action?.payload?.message;
            }
        });
        // change applicant rating
        builder.addCase(updateApplicantRatingInProfile.pending, (state, action) => {
            if (state.applicantDetails) {
                state.applicantDetails.updateRatingStatus = 'pending';
            }
        });
        builder.addCase(updateApplicantRatingInProfile.fulfilled, (state, action) => {
            if (state.applicantDetails) {
                state.applicantDetails.updateRatingStatus = 'success';
                state.applicantDetails.updateRatingResponse = action?.payload?.message;
                state.applicantDetails.rating = action.meta.arg.payload.rating;
            }
        });
        builder.addCase(updateApplicantRatingInProfile.rejected, (state, action) => {
            if (state.applicantDetails) {
                state.applicantDetails.updateRatingStatus = 'failed';
                state.applicantDetails.updateRatingResponse = action?.payload?.message;
            }
        });

          //Handle fetching eeo profile candidate answers
          builder.addCase(fetchCandidateProfileEEOQuestionsAnswers.pending, (state) => {
            state.fetchEEOQuestionsStatus = 'pending'; 
          });
          builder.addCase(fetchCandidateProfileEEOQuestionsAnswers.fulfilled, (state, action) => {
            state.fetchEEOQuestionsStatus = 'success'; 
            state.eeoQuestionAndAnswers = action.payload.data; 
          });
          builder.addCase(fetchCandidateProfileEEOQuestionsAnswers.rejected, (state, action) => {
            state.fetchEEOQuestionsStatus = 'failed'; 
          });

        // Handle fetching additional question answers
        builder.addCase(fetchCandidateProfileAdditionalQuestionAnswers.pending, (state) => {
            state.fetchAdditionalQuestionsStatus = 'pending';
        });
        builder.addCase(fetchCandidateProfileAdditionalQuestionAnswers.fulfilled, (state, action) => {
            state.fetchAdditionalQuestionsStatus = 'success';
            // Save the additional questions with their answers and autoReject flag
            state.additionalQuestions = action.payload.data;
        });
        builder.addCase(fetchCandidateProfileAdditionalQuestionAnswers.rejected, (state, action) => {
            state.fetchAdditionalQuestionsStatus = 'failed';
            state.fetchAdditionalQuestionsResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });

          // Handle fetching additional documents
        builder.addCase(fetchCandidateExtraDocument.pending, (state) => {
            state.fetchCandidateDocumentStatus = 'pending';
        });
        builder.addCase(fetchCandidateExtraDocument.fulfilled, (state, action) => {
            state.fetchCandidateDocumentStatus = 'success';
            state.candidateDocumentURL = action.payload;
        });
        builder.addCase(fetchCandidateExtraDocument.rejected, (state, action) => {
            state.fetchAndGenerateAiResumeFocusedQuestionsStatus = 'failed';
            state.fetchCandidateDocumentResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });

        // Generating Resume focused questions by ai
        builder.addCase(fetchAndGenerateAiResumeFocusedQuestions.pending, (state) => {
            state.fetchAndGenerateAiResumeFocusedQuestionsStatus = 'pending';
        });
        builder.addCase(fetchAndGenerateAiResumeFocusedQuestions.fulfilled, (state, action) => {
            state.fetchAndGenerateAiResumeFocusedQuestionsStatus = 'success';
            state.aiInterviewQuestions = action.payload.data;
        });
        builder.addCase(fetchAndGenerateAiResumeFocusedQuestions.rejected, (state, action) => {
            state.fetchAndGenerateAiResumeFocusedQuestionsStatus = 'failed';
            state.fetchAndGenerateAiResumeFocusedQuestionsResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });

        // Saving interview questions, anwsers
        builder.addCase(saveInterviewQuestionResponses.pending, (state) => {
            state.saveInterviewAnswersStatus = 'pending';
        });
        builder.addCase(saveInterviewQuestionResponses.fulfilled, (state, action) => {
            state.saveInterviewAnswersStatus = 'success';
        });
        builder.addCase(saveInterviewQuestionResponses.rejected, (state, action) => {
            state.saveInterviewAnswersStatus = 'failed';
            state.saveInterviewAnswersResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });


        // Generate & Fetch Ai Interview Kit (HTML FORMAT RESPONSE)
        builder.addCase(fetchAndGenerateAiInterivewKit.pending, (state) => {
            state.fetchAndGenerateAiInterivewKitStatus = 'pending';
        });
        builder.addCase(fetchAndGenerateAiInterivewKit.fulfilled, (state, action) => {
            state.fetchAndGenerateAiInterivewKitStatus = 'success';
            state.aiInterviewHTMLKit = action.payload.data;
        });
        builder.addCase(fetchAndGenerateAiInterivewKit.rejected, (state, action) => {
            state.fetchAndGenerateAiInterivewKitStatus = 'failed';
            state.fetchAndGenerateAiInterivewKitResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });


        // Get Applicant Inbox
        builder.addCase(getApplicantInbox.pending, (state) => {
            state.getApplicantInboxStatus = 'pending';
          });
          
        builder.addCase(getApplicantInbox.fulfilled, (state, action) => {
            state.getApplicantInboxStatus = 'success';
            state.applicantInboxMessages = action.payload.messages;
        });
        
        builder.addCase(getApplicantInbox.rejected, (state, action) => {
            state.getApplicantInboxStatus = 'failed';
            state.getApplicantInboxResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });
          
    }
});

export const { resetGetApplicantResume, resetAiCandidateInterviewKit, resetApplicantInbox, resetAiCandidateInterviewQuestions, resetUpdateApplicantProfile, resetGetApplicantDetails,
    resetChangeApplicantStageInProfile, resetUpdateApplicantRatingInProfile, resetApplicantProfile } = applicantProfileSlice.actions;
export default applicantProfileSlice;
